import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import type { MetricsDataset } from '@/models/OeeMetrics';

export const formatTimeMetric = (seconds: number): string => {
  if (seconds === 0) {
    return '0s';
  }
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  if (seconds < 3600) {
    return `${(seconds / 60).toFixed(1)}min`;
  }
  return `${(seconds / 3600).toFixed(1)}h`;
};

export const getBaseChartOptions = (
  yAxisTitle: string,
  tooltipLabelCallback: (tooltipItem: TooltipItem<'bar'>) => string,
): ChartOptions<'bar'> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',

      onClick: () => {},
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        title: (tooltipItems: TooltipItem<'bar'>[]) => {
          const rawDate = tooltipItems[0]?.parsed?.x;
          if (!rawDate) {
            return '';
          }
          const date = new Date(rawDate);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const dateStr = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          return `${dateStr} ${isWeekend ? '(Weekend)' : '(Weekday)'}`;
        },
        label: tooltipLabelCallback,
      },
    },
  },
  scales: {
    x: {
      type: 'time',
      time: {
        unit: 'day',
      },
      title: {
        display: true,
        text: 'Date',
      },
      grid: {
        display: true,
        color: (context) => {
          if (!context.tick?.value) {
            return 'rgba(0, 0, 0, 0.1)';
          }
          const date = new Date(context.tick.value);
          return date.getDay() === 0 || date.getDay() === 6
            ? 'rgba(66, 135, 245, 0.1)'
            : 'rgba(0, 0, 0, 0.1)';
        },
      },
    },
    y: {
      beginAtZero: true,
      display: true,
      title: {
        display: true,
        text: yAxisTitle,
      },
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.1)',
      },
    },
  },
});

export const getEmptyChartData = (): ChartData<'bar'> => ({
  labels: [] as Date[],
  datasets: [] as {
    label: string;
    data: number[];
    backgroundColor: string;
    stack: string;
  }[],
});

export const getTimeChartOptions = (): ChartOptions<'bar'> => {
  const baseOptions = getBaseChartOptions(
    'Hours (24h day)',
    (tooltipItem: TooltipItem<'bar'>) => {
      const label = tooltipItem.dataset.label || '';
      const seconds = tooltipItem.raw as number;

      if (seconds >= 3600) {
        const hours = (seconds / 3600).toFixed(1);
        return `${label}: ${hours} h`;
      }
      const minutes = Math.round(seconds / 60);
      return `${label}: ${minutes} min`;
    },
  );

  return {
    ...baseOptions,
    scales: {
      x: baseOptions.scales?.x ?? {
        type: 'time',
        time: { unit: 'day' },
        title: { display: true, text: 'Date' },
      },
      y: {
        ...(baseOptions.scales?.y ?? {}),
        stacked: true,
        max: 86400,
        ticks: {
          callback: (value) => {
            return `${Math.round(Number(value) / 3600)}h`;
          },
        },
      },
    },
  };
};

export const getCountChartOptions = (): ChartOptions<'bar'> => {
  return getBaseChartOptions('Count', (tooltipItem: TooltipItem<'bar'>) => {
    const label = tooltipItem.dataset.label || '';
    const count = tooltipItem.raw as number;
    return `${label}: ${count}`;
  });
};

export const createDataset = (
  eventType: string,
  data: number[],
  colorMap: Record<string, string>,
  labelMap: Record<string, string>,
  options: { stack?: string; border?: boolean } = {},
): MetricsDataset => {
  const color = colorMap[eventType] || '#ccc';
  return {
    label: labelMap[eventType] || eventType,
    data,
    backgroundColor: color,
    ...(options.border
      ? {
          borderColor: color,
          borderWidth: 1,
        }
      : {}),
    ...(options.stack ? { stack: options.stack } : {}),
  };
};

export const updateChartDatasets = (
  datasetMap: Record<string, number[]>,
  colorMap: Record<string, string>,
  labelMap: Record<string, string>,
  options: { stack?: string; border?: boolean } = {},
): ChartData<'bar'>['datasets'] => {
  return Object.entries(datasetMap).map(([eventType, data]) =>
    createDataset(eventType, data, colorMap, labelMap, options),
  );
};
